import AccessRights from "utils/AccessRights";
import { F } from "pages/AppraisalManagment/constants/legacyApiKeys";
import { calculateAppraisalSummaryPercentage, formatMaxRatingLabel } from "pages/AppraisalManagment/utils/appraisalProgressSummary";

export { formatMaxRatingLabel };

/** Row-level status permissions from API (`appraisal_status_level_permissions`). */
export const resolveStatusLevelPermission = (row, actionKey) => {
  const statusPermissions = row?.appraisal_status_level_permissions;
  if (!statusPermissions || typeof statusPermissions !== "object") {
    return true;
  }

  const map = {
    view: ["can_view", "canView"],
    edit: ["can_edit", "canEdit"],
    delete: ["can_delete", "canDelete"],
  };

  const keys = map[actionKey] || [];
  for (const key of keys) {
    const value = statusPermissions[key];
    if (value === true || value === 1 || value === "1") return true;
    if (value === false || value === 0 || value === "0") return false;
  }

  return true;
};

const mergePermissionFlag = (apiFlag, pageAllowed, defaultWhenMissing) => {
  if (apiFlag === true || apiFlag === 1 || apiFlag === "1") return true;
  if (apiFlag === false || apiFlag === 0 || apiFlag === "0") return false;
  if (pageAllowed === true) return true;
  if (pageAllowed === false) return false;
  return defaultWhenMissing;
};

/** Workflow status labels — `status` / `display_status` only (not employee signature). */
export const WORKFLOW_STATUS_LABEL = Object.freeze({
  sent_to_rtm: "Sent to RTM",
  sent_rtm: "Sent to RTM",
  returned_to_supervisor: "Returned to Supervisor",
  completed: "Completed",
  approved: "Approved",
  auto_approved: "Auto Approved",
  level_one_approved: "Level One Approved",
  level_one_rejected: "Level One Rejected",
  pending: "Pending",
});

/** Employee signature column labels — `employee_signature_status` / confirmation only. */
export const EMPLOYEE_SIGNATURE_STATUS_LABEL = Object.freeze({
  pending: "Pending Employee Signature",
  awaiting_employee: "Pending Employee Signature",
  approved: "Signed By Employee",
  signed: "Signed By Employee",
  confirmed: "Signed By Employee",
  rejected: "Rejected By Employee",
});

/** Edit on My PAFs list — controlled only by API `can_edit`. */
export const canEmployeeEditPaf = (row) => {
  const value = row?.can_edit ?? row?.canEdit;
  return value === 1 || value === "1" || value === true;
};

/**
 * Centralized View / Edit / Delete rules for My PAFs list actions.
 * Edit/Sign is driven only by row `can_edit`; view/delete still use page + row flags.
 */
export const getMyPafRowPermissions = (row, session) => {
  const currentPermissions = session?.current_page_permissions;
  const hasPagePermissions =
    currentPermissions &&
    !Array.isArray(currentPermissions) &&
    Object.keys(currentPermissions).length > 0;

  const createdBy = row?.created_by ?? row?.created_by_id ?? session?.user?.id ?? "";

  const pageCanView =
    !hasPagePermissions ||
    (AccessRights(currentPermissions, "view", createdBy) &&
      resolveStatusLevelPermission(row, "view"));

  const pageCanDelete =
    hasPagePermissions &&
    AccessRights(currentPermissions, "delete", createdBy) &&
    resolveStatusLevelPermission(row, "delete");

  return {
    canView: mergePermissionFlag(row?.can_view, pageCanView, true),
    canEdit: canEmployeeEditPaf(row),
    canDelete:
      row?.is_deleted !== "y" && mergePermissionFlag(row?.can_delete, pageCanDelete, false),
  };
};

export const getCurrentEmployeeId = (session) => {
  if (!session?.user) return null;
  return (
    session.user.employee_id ??
    session.user.employeeId ??
    session.user.id ??
    session.user.user_id ??
    null
  );
};

const normalizeStatusKey = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "_");

const formatStatusLabel = (value) =>
  String(value || "")
    .trim()
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());

const resolvePersonDisplayName = (value) => {
  if (!value) return "";
  if (typeof value === "string") return value.trim();
  if (typeof value === "object") {
    const nameParts = [value.firstname, value.middlename, value.lastname]
      .map((part) => (part == null ? "" : String(part).trim()))
      .filter(Boolean);
    if (nameParts.length > 0) return nameParts.join(" ");
    return String(
      value.full_name || value.name || value.employee_name || value.label || ""
    ).trim();
  }
  return "";
};

const parseSectionDataBlob = (value) => {
  if (value == null || value === "") return null;
  if (typeof value === "string") {
    try {
      return JSON.parse(value);
    } catch {
      return null;
    }
  }
  return value;
};

/** Normalize a section group value to a flat array of scored rows. */
export const normalizeSectionGroupRows = (groupValue) => {
  if (Array.isArray(groupValue)) {
    return groupValue.filter(Boolean);
  }
  if (groupValue && typeof groupValue === "object") {
    if (Array.isArray(groupValue.questions)) return groupValue.questions.filter(Boolean);
    if (Array.isArray(groupValue.categories)) return groupValue.categories.filter(Boolean);
    if (Array.isArray(groupValue.items)) return groupValue.items.filter(Boolean);
    if (groupValue.title || groupValue.obtained_score != null || groupValue.obtained_rating != null) {
      return [groupValue];
    }
  }
  return [];
};

const mapSectionCategoryRow = (categoryItem, groupTitle, index) => ({
  id: categoryItem.id || `row-${groupTitle}-${index}`,
  title: categoryItem.title || categoryItem.name || categoryItem[F.legacyNameSynonym] || groupTitle,
  rating: categoryItem.rating ?? categoryItem.total_rating ?? null,
  weightage: categoryItem.weightage ?? null,
  description: categoryItem.description || "",
  obtained_score: categoryItem.obtained_score ?? categoryItem.obtained_rating ?? null,
  comments: categoryItem.comments ?? "",
  [F.setupPivotKey]: categoryItem.id || `row-${groupTitle}-${index}`,
  [F.titleSynonym]: groupTitle,
});

/** Merge `sections_data` with optional `individual_objectives` / `core_values` groups. */
export const resolveEmployeePafSectionsData = (record) => {
  const base = {};

  const assignGroup = (groupTitle, groupValue) => {
    const normalizedTitle = String(groupTitle || "").trim().toLowerCase();
    if (
      !groupTitle ||
      normalizedTitle === "overall_score" ||
      normalizedTitle === "rating_summary" ||
      normalizedTitle === "rating summary"
    ) {
      return;
    }
    const rows = normalizeSectionGroupRows(groupValue);
    if (rows.length > 0) {
      base[groupTitle] = rows;
    }
  };

  const rawBlob =
    parseSectionDataBlob(record?.sections_data) ??
    parseSectionDataBlob(record?.[F.sectionDataSynonym]) ??
    parseSectionDataBlob(record?.[F.camelData]);

  if (rawBlob && typeof rawBlob === "object" && !Array.isArray(rawBlob)) {
    Object.entries(rawBlob).forEach(([groupTitle, groupValue]) => assignGroup(groupTitle, groupValue));
  } else if (Array.isArray(rawBlob) && rawBlob.length > 0) {
    rawBlob.forEach((sectionRow, index) => {
      const groupTitle =
        sectionRow?.title || sectionRow?.[F.titleSynonym] || sectionRow?.name || `Section ${index + 1}`;
      const rows = normalizeSectionGroupRows(sectionRow.categories ?? sectionRow);
      if (rows.length > 0) {
        assignGroup(groupTitle, rows);
      }
    });
  }

  const mergeGroup = (source, fallbackTitle) => {
    if (!source) return;
    if (typeof source === "object" && !Array.isArray(source)) {
      Object.entries(source).forEach(([groupTitle, groupValue]) => assignGroup(groupTitle, groupValue));
      return;
    }
    assignGroup(fallbackTitle, source);
  };

  mergeGroup(record?.individual_objectives, "Individual Objectives");
  mergeGroup(record?.core_values, "Core Values");
  return base;
};

/** Prefer API detail but keep navigation list sections when detail omits scoring blob. */
export const mergeMyPafViewRecord = (navigationRecord, apiRecord) => {
  if (!navigationRecord?.id) return apiRecord || navigationRecord || {};
  if (!apiRecord?.id) return navigationRecord;
  if (String(navigationRecord.id) !== String(apiRecord.id)) return apiRecord;

  const navSections = resolveEmployeePafSectionsData(navigationRecord);
  const apiSections = resolveEmployeePafSectionsData(apiRecord);
  const sections_data =
    Object.keys(apiSections).length > 0
      ? apiSections
      : Object.keys(navSections).length > 0
        ? navSections
        : apiSections;

  return normalizeMyPafRecord({
    ...navigationRecord,
    ...apiRecord,
    sections_data,
  });
};

/** Snake_case employee signature key from API fields only. */
export const getEmployeeSignatureStatusKey = (record) => {
  if (!record) return "pending";

  const raw = record.employee_signature_status ?? record.employee_confirmation_status;
  if (raw !== null && raw !== undefined && String(raw).trim() !== "") {
    return normalizeStatusKey(raw);
  }

  if (record.employee_signed_at) return "approved";
  return "pending";
};

export const isEmployeePafSigned = (record) => {
  if (!record) return false;

  const key = getEmployeeSignatureStatusKey(record);
  if (key === "rejected") return false;
  if (key === "approved" || key === "signed" || key === "confirmed") return true;

  return Boolean(
    record.employee_signed_at ||
      record.employee_signed_at_formatted ||
      record.employee_signature_date
  );
};

/** Whether the employee can submit approve/sign (`can_sign` from API). */
export const canEmployeeSignPaf = (record) => {
  if (!record) return false;

  const signatureKey = getEmployeeSignatureStatusKey(record);
  if (signatureKey === "approved" || signatureKey === "signed" || signatureKey === "confirmed") {
    return false;
  }

  if (record.can_sign === false || record.can_sign === 0 || record.can_sign === "0") {
    return false;
  }
  if (record.can_sign === true || record.can_sign === 1 || record.can_sign === "1") {
    return true;
  }

  return false;
};

/** Status text for employee-signature tab (`employee_confirmation_status` column). */
export const getEmployeeConfirmationStatusDisplay = (record) => {
  const raw = record?.employee_confirmation_status;
  if (raw === null || raw === undefined || String(raw).trim() === "") {
    return isEmployeePafSigned(record) ? "Signed" : "Pending";
  }

  const key = normalizeStatusKey(raw);
  if (key === "signed" || key === "approved" || key === "confirmed" || key === "completed") {
    return "Signed";
  }
  if (key === "pending" || key === "awaiting_employee") {
    return "Pending Employee Signature";
  }
  if (key === "rejected") return "Rejected";
  return formatStatusLabel(raw);
};

export const getEmployeeSignatureStatusLabel = (record) => {
  const key = getEmployeeSignatureStatusKey(record);
  if (EMPLOYEE_SIGNATURE_STATUS_LABEL[key]) {
    return EMPLOYEE_SIGNATURE_STATUS_LABEL[key];
  }
  return formatStatusLabel(key) || EMPLOYEE_SIGNATURE_STATUS_LABEL.pending;
};

/** Workflow status key from `display_status` or `status` only. */
export const getMyPafWorkflowStatusKey = (record) => {
  const raw =
    record?.display_status ??
    record?.status ??
    record?.appraisal_status ??
    record?.paf_status ??
    record?.workflow_status ??
    "";

  let statusText = "";
  if (raw !== undefined && raw !== null && raw !== "") {
    if (typeof raw === "string") {
      statusText = raw;
    } else if (typeof raw === "number") {
      statusText = String(raw);
    } else if (typeof raw === "object") {
      statusText = raw?.name || raw?.label || "";
    }
  }

  let key = normalizeStatusKey(statusText);
  if (key === "sent_rtm") key = "sent_to_rtm";
  return key || "pending";
};

export const getMyPafStatusLabel = (record) => {
  const key = getMyPafWorkflowStatusKey(record);
  if (WORKFLOW_STATUS_LABEL[key]) {
    return WORKFLOW_STATUS_LABEL[key];
  }
  if (key.includes("completed")) return WORKFLOW_STATUS_LABEL.completed;
  return formatStatusLabel(key) || WORKFLOW_STATUS_LABEL.pending;
};

export const getMyPafWorkflowStatusBadgeClass = (record) => {
  const key = getMyPafWorkflowStatusKey(record);
  if (key === "sent_to_rtm") return "badge bg-primary text-capitalize";
  if (key === "level_one_approved" || key === "completed") {
    return "badge bg-success text-capitalize";
  }
  if (key === "auto_approved") return "badge text-capitalize";
  if (key === "level_one_rejected") return "badge bg-danger text-capitalize";
  if (key === "returned_to_supervisor") return "badge bg-secondary text-capitalize";
  if (key === "approved") return "badge bg-success text-capitalize";
  return "badge bg-secondary text-capitalize";
};

export const getEmployeeSignatureStatusBadgeClass = (record) => {
  const key = getEmployeeSignatureStatusKey(record);
  if (key === "approved" || key === "signed" || key === "confirmed") {
    return "badge bg-success text-capitalize";
  }
  if (key === "rejected") return "badge bg-danger text-capitalize";
  return "badge bg-warning text-dark text-capitalize";
};

/** Client-side filter for employee signature column. */
export const filterMyPafsByEmployeeSignature = (rows, signatureStatus) => {
  if (!signatureStatus || !Array.isArray(rows)) return rows;
  const target = normalizeStatusKey(signatureStatus);
  return rows.filter((row) => getEmployeeSignatureStatusKey(row) === target);
};

/** Appraisal setup / form title shown in My PAFs list. */
export const getPafNameLabel = (record) => {
  const name = String(
    record?.setup_name ||
      record?.paf_name ||
      record?.appraisal_form_name ||
      record?.appraisal_name ||
      record?.form_name ||
      ""
  ).trim();

  if (name) return name;
  return getPafTypeLabel(record);
};

export const getPafTypeLabel = (record) => {
  const value =
    record?.appraisal_types ||
    record?.appraisal_type ||
    record?.frequency ||
    record?.paf_type ||
    "";

  if (!value) {
    const setupTitle =
      record?.setup_name ||
      record?.appraisal_name ||
      record?.employee?.appraisal_search_text ||
      "";
    return setupTitle || "PAF";
  }

  return String(value).trim();
};

export const getMonthYearLabel = (record) => {
  if (record?.period_label) {
    return String(record.period_label).trim();
  }

  if (Array.isArray(record?.months) && record.months.length > 0) {
    const months = record.months.map((m) => String(m).trim()).filter(Boolean);
    const year = getYearFromRecord(record);
    return year ? `${months.join(", ")} ${year}` : months.join(", ");
  }

  const month =
    record?.month ||
    record?.appraisal_month ||
    record?.month_name ||
    "";

  const year = getYearFromRecord(record);
  if (month && year) return `${month} ${year}`;
  if (month) return String(month);
  if (year) return String(year);

  const dateValue = record?.appraisal_date_time || record?.created_at;
  if (!dateValue) return "";
  const parsed = new Date(dateValue);
  if (Number.isNaN(parsed.getTime())) return "";
  return parsed.toLocaleDateString("en-US", { month: "long", year: "numeric" });
};

const getYearFromRecord = (record) => {
  if (record?.year) return record.year;
  const dateValue = record?.appraisal_date_time || record?.created_at;
  if (!dateValue) return "";
  const parsed = new Date(dateValue);
  if (Number.isNaN(parsed.getTime())) return "";
  return parsed.getFullYear();
};

/** Map section title (lowercase) → category weight from `sections` / legacy KPI list rows. */
export const buildSectionWeightageMap = (record) => {
  const map = {};
  (record?.sections || record?.[F.listAlternatePlural] || []).forEach((sectionRow) => {
    const key = String(sectionRow?.title || "").trim().toLowerCase();
    const weight = Number(sectionRow?.weightage);
    if (key && Number.isFinite(weight) && weight > 0) {
      map[key] = weight;
    }
  });
  return map;
};

/** Numeric overall % from API `scores_summary` (preferred) or calculated from section scores. */
export const resolveOverallScoreValue = (record) => {
  const summaryCandidates = [
    record?.scores_summary?.overall_score,
    record?.scores_summary?.summary_percentage,
    record?.scores_summary?.summary,
  ];

  for (const candidate of summaryCandidates) {
    if (candidate === null || candidate === undefined || candidate === "") continue;
    const numeric = Number(candidate);
    if (Number.isFinite(numeric)) return numeric;
  }

  const rootOverall = record?.overall_score;
  if (rootOverall !== null && rootOverall !== undefined && rootOverall !== "") {
    const numeric = Number(rootOverall);
    if (Number.isFinite(numeric)) return numeric;
  }

  const sectionCategories = buildSectionCategoriesFromRecord(record);
  if (!Object.keys(sectionCategories).length) return null;

  const calculated = calculateAppraisalSummaryPercentage({
    sectionCategories,
    sectionData: record?.sections_data || record?.[F.sectionDataSynonym] || {},
    sectionWeightageByTitle: buildSectionWeightageMap(record),
  });

  return Number.isFinite(calculated) ? calculated : null;
};

export const getSupervisorNameFromRecord = (record) => {
  const fromAppraiserDetails = resolvePersonDisplayName(record?.appraiser_details);
  if (fromAppraiserDetails) return fromAppraiserDetails;

  const directName = String(
    record?.supervisor_name ||
      record?.appraiser_name ||
      record?.supervisor_full_name ||
      ""
  ).trim();
  if (directName) return directName;

  const supervisor =
    record?.appraiser ||
    record?.appraiser_details ||
    record?.employee?.supervisor ||
    record?.supervisor ||
    null;

  if (!supervisor) return "";
  if (typeof supervisor === "string") return supervisor.trim();

  const nameParts = [supervisor.firstname, supervisor.middlename, supervisor.lastname]
    .map((part) => (part == null ? "" : String(part).trim()))
    .filter(Boolean);

  if (nameParts.length > 0) return nameParts.join(" ");
  return String(supervisor.full_name || supervisor.name || supervisor.employee_name || "").trim();
};

export const getOverallScoreLabel = (record) => {
  const score = resolveOverallScoreValue(record);
  if (score === null || score === undefined) return "—";
  return `${Math.round(score)}%`;
};

const capitalizeMonth = (value) => {
  const text = String(value || "").trim();
  if (!text) return "";
  return `${text.charAt(0).toUpperCase()}${text.slice(1).toLowerCase()}`;
};

/** Normalize list/detail API row for My PAFs UI (`/api/employee-pafs`). */
export const normalizeMyPafRecord = (item) => {
  if (!item) return item;

  const employee = item.employee_details || item.employee || {};
  const employeeName = resolvePersonDisplayName(employee) || item.employee_name || item.name || "";

  const monthValues = Array.isArray(item.months)
    ? item.months
    : item.month
      ? String(item.month).split(",").map((m) => m.trim()).filter(Boolean)
      : [];

  const capitalizedSingleMonth = item?.month ? capitalizeMonth(item.month) : "";
  const sectionsData = resolveEmployeePafSectionsData(item);
  const mergedForScore = { ...item, sections_data: sectionsData };
  const apiSummaryScore = item?.scores_summary?.overall_score;
  const hasApiSummaryScore =
    apiSummaryScore !== null &&
    apiSummaryScore !== undefined &&
    apiSummaryScore !== "" &&
    Number.isFinite(Number(apiSummaryScore));
  const overallScore = hasApiSummaryScore
    ? Number(apiSummaryScore)
    : resolveOverallScoreValue(mergedForScore);
  const supervisorName = getSupervisorNameFromRecord(item);
  const mergedRow = { ...item, sections_data: sectionsData, overall_score: overallScore };
  const canSign =
    item.can_sign !== undefined && item.can_sign !== null
      ? item.can_sign === true || item.can_sign === 1 || item.can_sign === "1"
      : canEmployeeSignPaf(mergedRow);

  return {
    ...item,
    employee: item.employee || employee,
    employee_details: item.employee_details || employee,
    appraiser_details: item.appraiser_details || item.appraiser || null,
    sections_data: sectionsData,
    name: employeeName,
    employee_name: employeeName,
    paf_name: getPafNameLabel(item),
    supervisor_name: supervisorName || item.supervisor_name || "",
    month: monthValues.length ? monthValues.join(", ") : capitalizedSingleMonth,
    months: monthValues,
    period_label:
      item.period_label ||
      (capitalizedSingleMonth && item.year
        ? `${capitalizedSingleMonth} ${item.year}`
        : capitalizedSingleMonth),
    overall_score: overallScore,
    scores_summary: {
      ...(item.scores_summary || {}),
      overall_score: hasApiSummaryScore ? Number(apiSummaryScore) : overallScore,
    },
    employee_signature_status:
      item.employee_signature_status || item.employee_confirmation_status || null,
    can_sign: canSign,
    can_edit:
      item.can_edit !== undefined && item.can_edit !== null
        ? item.can_edit
        : item.canEdit,
  };
};

const appendCategoriesFromBlob = (categoriesMap, blob) => {
  if (!blob || typeof blob !== "object" || Array.isArray(blob)) return;

  Object.keys(blob).forEach((groupTitle) => {
    if (String(groupTitle).toLowerCase() === "overall_score") return;

    const categories = normalizeSectionGroupRows(blob[groupTitle]);
    if (!categories.length) return;

    categoriesMap[groupTitle] = categories.map((categoryItem, index) =>
      mapSectionCategoryRow(categoryItem, groupTitle, index)
    );
  });
};

const appendCategoriesFromSectionList = (categoriesMap, record) => {
  const sectionList = record?.sections ?? record?.[F.listAlternatePlural] ?? [];
  if (!Array.isArray(sectionList) || !sectionList.length) return;

  sectionList.forEach((sectionRow, index) => {
    const groupTitle =
      sectionRow?.title || sectionRow?.[F.titleSynonym] || sectionRow?.name || `Section ${index + 1}`;
    const categories = normalizeSectionGroupRows(
      sectionRow?.categories ?? sectionRow?.category_rows ?? sectionRow
    );
    if (!categories.length) return;

    categoriesMap[groupTitle] = categories.map((categoryItem, rowIndex) =>
      mapSectionCategoryRow(categoryItem, groupTitle, rowIndex)
    );
  });
};

const appendCategoriesFromAppraisalSetups = (categoriesMap, record) => {
  const setups = record?.appraisal_setups;
  if (!Array.isArray(setups)) return;

  setups.forEach((setup) => {
    const pivotRows = setup?.[F.setupPivotRows] ?? setup?.setup_sections;
    if (!Array.isArray(pivotRows)) return;

    pivotRows.forEach((setupRow) => {
      const groupTitle = setupRow?.[F.pivotRelation]?.title || setupRow?.title || "Section";
      const savedRow =
        record?.sections_data?.[setupRow.id] ||
        record?.[F.sectionDataSynonym]?.[setupRow.id] ||
        {};
      categoriesMap[groupTitle] = categoriesMap[groupTitle] || [];
      categoriesMap[groupTitle].push(
        mapSectionCategoryRow(
          {
            id: setupRow.id,
            title: groupTitle,
            rating: savedRow.rating ?? setupRow.rating ?? null,
            weightage: savedRow.weightage ?? setupRow.weightage ?? null,
            description: savedRow.description ?? setupRow?.[F.pivotRelation]?.description ?? "",
            obtained_score: savedRow.obtained_score ?? savedRow.obtained_rating ?? null,
            comments: savedRow.comments ?? "",
          },
          groupTitle,
          categoriesMap[groupTitle].length
        )
      );
    });
  });
};

export const buildSectionCategoriesFromRecord = (record) => {
  const categoriesMap = {};
  const blob = resolveEmployeePafSectionsData(record);

  appendCategoriesFromBlob(categoriesMap, blob);

  if (!Object.keys(categoriesMap).length) {
    appendCategoriesFromSectionList(categoriesMap, record);
  }
  if (!Object.keys(categoriesMap).length) {
    appendCategoriesFromAppraisalSetups(categoriesMap, record);
  }

  return categoriesMap;
};

export const plainTextFromHtml = (value) => {
  if (value === null || value === undefined) return "";
  if (typeof value !== "string") return String(value).trim();
  return value
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/\s+/g, " ")
    .trim();
};

/** Supervisor / appraiser comments from employee PAF API (`supervisor_comments` preferred). */
export const getSupervisorCommentsFromRecord = (record) => {
  const candidates = [
    record?.supervisor_comments,
    record?.appraisers_comments,
    record?.appraisersComments,
    record?.supervisorComments,
  ];

  for (const item of candidates) {
    const text = plainTextFromHtml(item);
    if (text) return text;
  }
  return "";
};

export const getManagerCommentsFromRecord = (record) => {
  const candidates = [
    record?.manager_comments,
    record?.operations_manager_comments,
    record?.hr_comments,
    record?.next_management_level_comments,
    record?.next_level_management_comments,
  ];

  for (const item of candidates) {
    const text = plainTextFromHtml(item);
    if (text) return text;
  }
  return "";
};

const parseRecordDateTime = (record) => {
  const dateTimeValue = record?.appraisal_date_time || record?.appraisalDateTime;
  const formattedDate = record?.appraisal_date_time_formatted;

  if (dateTimeValue) {
    try {
      if (dateTimeValue instanceof Date) return dateTimeValue;
      const parsed = new Date(dateTimeValue);
      if (!Number.isNaN(parsed.getTime())) return parsed;
    } catch {
      /* fall through */
    }
  }

  if (formattedDate) {
    try {
      const isoDate = String(formattedDate).replace(" ", "T") + ":00";
      const parsed = new Date(isoDate);
      if (!Number.isNaN(parsed.getTime())) return parsed;
      const parsedAlt = new Date(String(formattedDate).replace(" ", "T"));
      if (!Number.isNaN(parsedAlt.getTime())) return parsedAlt;
    } catch {
      /* fall through */
    }
  }

  return null;
};

/** Map My PAF API record into appraisal-progress form state for the employee sign screen. */
export const mapMyPafRecordToEmployeeFormState = (record) => {
  if (!record) {
    return {
      id: null,
      employeeName: "",
      appraisal_name: "",
      appraiserName: "",
      appraisalDateTime: null,
      appraisersComments: "",
      developmentalPlan: "",
      signOff: "",
      employeeSigned: false,
      employeeConfirmationStatus: "",
      employeeConfirmationStatusDisplay: "Pending",
      employeeSignedAt: null,
      signatureImage: "",
      sectionData: {},
      current_user_role: "employee",
    };
  }

  const employeeName =
    resolvePersonDisplayName(record.employee_details || record.employee) ||
    record.employee_name ||
    record.name ||
    "";

  const signed = isEmployeePafSigned(record);
  const sectionsData = resolveEmployeePafSectionsData(record);

  return {
    id: record.id,
    employeeName,
    appraisal_name: getPafNameLabel(record),
    appraiserName: getSupervisorNameFromRecord(record),
    appraisalDateTime: parseRecordDateTime(record),
    appraisersComments: getSupervisorCommentsFromRecord(record),
    developmentalPlan: record.developmental_plan || record.developmentalPlan || "",
    signOff: signed ? 1 : record.employee_sign_off || "",
    employeeSigned: signed,
    employeeConfirmationStatus: record.employee_confirmation_status ?? "",
    employeeConfirmationStatusDisplay: getEmployeeConfirmationStatusDisplay(record),
    employeeSignedAt: record.employee_signed_at ?? null,
    signatureImage:
      record.employee_signature ||
      record.employee_signature_image ||
      record.signature_image ||
      "",
    sectionData: sectionsData,
    current_user_role: "employee",
  };
};

export const formatSignedDate = (record) => {
  if (record?.employee_signed_at_formatted) {
    return String(record.employee_signed_at_formatted).trim();
  }

  const raw =
    record?.employee_signed_at ||
    record?.employee_signature_date ||
    record?.employee_sign_off_date_time ||
    null;

  if (!raw) return "";
  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) return String(raw);
  return parsed.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};
